import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { API_BASE } from '@/helpers/endpoints';

const SESSION_KEY = 'usage.analytics.session.v1';
const QUEUE_KEY = 'usage.analytics.queue.v1';
const cfg = window.__APP_CONFIG__ || {};
const number = (value, fallback) => value !== '' && value !== null && value !== undefined
  && Number.isFinite(Number(value)) ? Number(value) : fallback;
const enabled = String(cfg.usageAnalyticsEnabled ?? process.env.VUE_APP_USAGE_ANALYTICS_ENABLED ?? 'true') !== 'false';
const settings = Object.freeze({
  endpoint: cfg.usageAnalyticsEndpoint || `${API_BASE}/api/reporting/usage/events/batch`,
  heartbeatMs: number(cfg.usageAnalyticsHeartbeatMs ?? process.env.VUE_APP_USAGE_ANALYTICS_HEARTBEAT_MS, 60000),
  inactivityMs: number(cfg.usageAnalyticsInactivityMs ?? process.env.VUE_APP_USAGE_ANALYTICS_INACTIVITY_MS, 300000),
  queueLimit: number(cfg.usageAnalyticsQueueLimit ?? process.env.VUE_APP_USAGE_ANALYTICS_QUEUE_LIMIT, 200),
  batchSize: number(cfg.usageAnalyticsBatchSize ?? process.env.VUE_APP_USAGE_ANALYTICS_BATCH_SIZE, 50),
});

class UsageAnalytics {
  constructor() {
    this.session = this.read(SESSION_KEY, null);
    this.queue = this.read(QUEUE_KEY, []);
    this.page = null;
    this.lastInteractionAt = Date.now();
    this.accountedAt = Date.now();
    this.focused = document.hasFocus();
    this.visible = !document.hidden;
    this.flushing = false;
    this.installed = false;
  }

  install(router) {
    if (!enabled || this.installed) return;
    this.installed = true;
    this.addActivityListeners();
    router.afterEach((to, from, failure) => {
      if (!failure) this.routeEntered(to, from);
    });
    router.beforeEach((to) => {
      if (this.isLogout(to)) this.endSession('LOGOUT');
      return true;
    });
    this.timer = window.setInterval(() => this.heartbeat(), settings.heartbeatMs);
    this.tick = window.setInterval(() => this.settle(Date.now()), 1000);
    window.addEventListener('pagehide', () => this.endSession('WINDOW_CLOSED', true));
  }

  routeEntered(to, from) {
    if (!sessionStorage.getItem('jwt') || !this.isMeaningful(to)) return;
    this.ensureSession();
    const next = this.routeDescriptor(to);
    if (this.page && this.page.key === next.key) return;
    const now = Date.now();
    this.settle(now);
    this.closePage(now, 'ROUTE_CHANGE');
    this.page = { ...next, enteredAt: now, activeDurationMs: 0,
      previousRouteName: from?.name ? String(from.name) : null };
    this.accountedAt = now;
    this.persistSession();
  }

  heartbeat() {
    if (!this.session || !sessionStorage.getItem('jwt')) return;
    const now = Date.now();
    this.settle(now);
    this.enqueue('SESSION_HEARTBEAT', this.pageFields(now));
    this.flush();
  }

  endSession(reason = 'UNKNOWN', keepalive = false) {
    if (!this.session) return;
    const now = Date.now();
    this.settle(now);
    this.closePage(now, reason === 'LOGOUT' ? 'LOGOUT' : 'SESSION_END');
    this.enqueue('SESSION_ENDED', { terminationReason: reason });
    this.flush(keepalive);
    this.session = null;
    this.page = null;
    sessionStorage.removeItem(SESSION_KEY);
  }

  ensureSession() {
    if (this.session) return;
    const now = Date.now();
    this.session = { analyticsSessionId: uuid(), startedAt: now, activeDurationMs: 0, sequenceNumber: 0 };
    this.accountedAt = now;
    this.enqueue('SESSION_STARTED');
    this.persistSession();
  }

  settle(now) {
    if (!this.session) { this.accountedAt = now; return; }
    if (this.visible && this.focused) {
      const activeUntil = Math.min(now, this.lastInteractionAt + settings.inactivityMs);
      const delta = Math.max(0, activeUntil - this.accountedAt);
      this.session.activeDurationMs += delta;
      if (this.page) this.page.activeDurationMs += delta;
    }
    this.accountedAt = now;
    this.persistSession();
  }

  closePage(now, navigationType) {
    if (!this.page || !this.session) return;
    const elapsed = Math.max(0, now - this.page.enteredAt);
    this.enqueue('PAGE_VISIT', {
      ...this.pageFields(now), enteredAt: new Date(this.page.enteredAt).toISOString(),
      exitedAt: new Date(now).toISOString(), elapsedDurationMs: elapsed,
      activeDurationMs: Math.min(elapsed, Math.max(0, Math.round(this.page.activeDurationMs))), navigationType,
      previousRouteName: this.page.previousRouteName,
    });
    this.page = null;
  }

  enqueue(eventType, extra = {}) {
    if (!this.session) return;
    this.session.sequenceNumber += 1;
    const now = Date.now();
    this.queue.push({
      eventId: uuid(), eventVersion: 1, eventType,
      analyticsSessionId: this.session.analyticsSessionId,
      sequenceNumber: this.session.sequenceNumber,
      occurredAt: new Date(now).toISOString(),
      sessionStartedAt: new Date(this.session.startedAt).toISOString(),
      lastActivityAt: new Date(this.lastInteractionAt).toISOString(),
      sessionActiveDurationMs: Math.max(0, Math.round(this.session.activeDurationMs)),
      ...extra,
    });
    if (this.queue.length > settings.queueLimit) this.queue.splice(0, this.queue.length - settings.queueLimit);
    this.persistQueue();
    this.persistSession();
  }

  async flush(keepalive = false) {
    const jwt = sessionStorage.getItem('jwt');
    if (!jwt || this.flushing || !this.queue.length) return;
    const batch = this.queue.slice(0, settings.batchSize);
    this.flushing = true;
    try {
      if (keepalive && window.fetch) {
        const response = await window.fetch(settings.endpoint, { method: 'POST', keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ events: batch }) });
        if (!response.ok) throw new Error(`Usage telemetry request failed: ${response.status}`);
      } else {
        await axios.post(settings.endpoint, { events: batch }, { headers: { Authorization: `Bearer ${jwt}` } });
      }
      const sent = new Set(batch.map((event) => event.eventId));
      this.queue = this.queue.filter((event) => !sent.has(event.eventId));
      this.persistQueue();
    } catch (_) {
      // Usage telemetry is best effort and must never affect application behavior.
    } finally {
      this.flushing = false;
    }
  }

  addActivityListeners() {
    let sampledAt = 0;
    const activity = () => {
      const now = Date.now();
      if (now - sampledAt < 1000) return;
      this.settle(now); sampledAt = now; this.lastInteractionAt = now;
    };
    ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'scroll'].forEach((name) =>
      window.addEventListener(name, activity, { passive: true }));
    document.addEventListener('visibilitychange', () => {
      const now = Date.now(); this.settle(now); this.visible = !document.hidden;
      if (this.visible) { this.lastInteractionAt = now; this.accountedAt = now; }
    });
    window.addEventListener('focus', () => {
      const now = Date.now(); this.settle(now); this.focused = true; this.lastInteractionAt = now; this.accountedAt = now;
    });
    window.addEventListener('blur', () => { const now = Date.now(); this.settle(now); this.focused = false; });
  }

  routeDescriptor(route) {
    const record = route.matched[route.matched.length - 1];
    const routeName = String(route.name || record?.name || record?.path || 'unnamed-route');
    const routePathTemplate = record?.path || route.path;
    return { key: `${routeName}|${routePathTemplate}`, routeName, routePathTemplate,
      pageName: route.meta?.analyticsName || route.meta?.title || routeName,
      module: route.meta?.analyticsModule || 'GENERAL' };
  }

  pageFields(now = Date.now()) {
    if (!this.page) return {};
    const elapsedDurationMs = Math.max(0, now - this.page.enteredAt);
    return { routeName: this.page.routeName, routePathTemplate: this.page.routePathTemplate,
      pageName: this.page.pageName, module: this.page.module,
      enteredAt: new Date(this.page.enteredAt).toISOString(),
      elapsedDurationMs,
      activeDurationMs: Math.min(elapsedDurationMs, Math.max(0, Math.round(this.page.activeDurationMs))) };
  }

  isMeaningful(route) {
    const excluded = ['login', 'logout', 'unauthorized', 'unauthorised'];
    return route.name && !excluded.includes(String(route.name).toLowerCase())
      && route.matched.some((record) => record.meta?.authRequired
        || record.meta?.analyticsTrackAuthenticated);
  }

  isLogout(route) { return String(route.name || '').toLowerCase().includes('logout'); }
  persistSession() { if (this.session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.session)); }
  persistQueue() { sessionStorage.setItem(QUEUE_KEY, JSON.stringify(this.queue)); }
  read(key, fallback) { try { return JSON.parse(sessionStorage.getItem(key)) ?? fallback; } catch (_) { return fallback; } }
}

export const usageAnalytics = new UsageAnalytics();
export const installUsageAnalytics = (router) => usageAnalytics.install(router);
